import { z } from 'zod';
import { GENDERS } from '../enums.js';
import { LEAVE_ACCRUALS, LEAVE_COUNT_BASES, LEAVE_LEDGER_ENTRIES, LEAVE_REQUEST_STATUSES, } from '../leave/enums.js';
import { isoDate, paginated, paginationQuerySchema, queryBoolean, queryList, uuid, } from './common.js';
/** Leave types, policy and balances (FR-LEAVE-1…3, ADR-055, ADR-076). */
/** Days in half-day steps. */
const halfDays = (max) => z
    .number()
    .min(0)
    .max(max)
    .refine((d) => Number.isInteger(d * 2), 'Use whole or half days');
const leaveTypeFields = z.object({
    name: z.string().trim().min(2).max(120),
    paid: z.boolean(),
    countBasis: z.enum(LEAVE_COUNT_BASES),
    /** Yearly quota; required for UPFRONT, otherwise null. */
    quotaDays: halfDays(366).nullable(),
    accrual: z.enum(LEAVE_ACCRUALS).nullable(),
    /** EARNED: one day per this many working days. */
    earnedPerWorkingDays: z.number().int().min(1).max(366).nullable(),
    /** Longest single request; null = no limit (ML 112, PL 14). */
    maxDaysPerRequest: halfDays(366)
        .refine((d) => d > 0, 'Must be more than 0')
        .nullable(),
    prorateJoiners: z.boolean(),
    confirmedOnly: z.boolean(),
    minServiceMonths: z.number().int().min(0).max(600),
    gender: z.enum(GENDERS).nullable(),
    allowHalfDay: z.boolean(),
    /** A document is required above this many days; null = never. */
    documentAboveDays: halfDays(366).nullable(),
    /** Days carried into the next year; null = nothing carried. */
    carryForwardCap: halfDays(366).nullable(),
    sortOrder: z.number().int().min(0).max(1000),
    active: z.boolean(),
});
/** The cross-field rules of a type (mirror the `leave_types` CHECKs, ADR-076). */
export function leaveTypeRuleIssues(t) {
    const issues = [];
    if (t.accrual === 'UPFRONT' && t.quotaDays === null) {
        issues.push({ path: 'quotaDays', message: 'Upfront leave needs a yearly quota' });
    }
    if (t.accrual !== 'UPFRONT' && t.quotaDays !== null) {
        issues.push({ path: 'quotaDays', message: 'Only upfront leave has a yearly quota' });
    }
    if ((t.accrual === 'EARNED') !== (t.earnedPerWorkingDays !== null)) {
        issues.push({
            path: 'earnedPerWorkingDays',
            message: t.accrual === 'EARNED'
                ? 'Earned leave needs its working-days rate'
                : 'Only earned leave has a working-days rate',
        });
    }
    if (t.prorateJoiners && t.accrual !== 'UPFRONT') {
        issues.push({ path: 'prorateJoiners', message: 'Only upfront quotas are prorated' });
    }
    if (t.carryForwardCap !== null && t.accrual === null) {
        issues.push({ path: 'carryForwardCap', message: 'Nothing to carry: this type has no balance' });
    }
    return issues;
}
const withRules = (schema) => schema.superRefine((value, ctx) => {
    for (const issue of leaveTypeRuleIssues(value)) {
        ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
    }
});
/** `POST /leave/types`. The code is fixed once created. */
export const leaveTypeCreateSchema = withRules(leaveTypeFields.extend({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9_]{0,19}$/, 'Use capital letters, digits and _ (max 20)'),
}));
/** `PATCH /leave/types/:id`: any subset; the merged type must still satisfy the rules. */
export const leaveTypeUpdateSchema = leaveTypeFields
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
/** A complete type for checking a merged update. */
export const leaveTypeRulesSchema = withRules(leaveTypeFields);
export const leaveTypeItemSchema = leaveTypeFields.extend({ id: uuid, code: z.string() });
export const leaveTypeListQuerySchema = z.object({ includeInactive: queryBoolean.default(false) });
export const leaveTypeListResponseSchema = z.object({ items: z.array(leaveTypeItemSchema) });
// ── Balances (FR-LEAVE-3) ─────────────────────────────────────────────────────────────────────
export const leaveBalanceQuerySchema = z.object({
    employeeId: uuid,
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const leaveLedgerEntrySchema = z.object({
    id: uuid,
    entryType: z.enum(LEAVE_LEDGER_ENTRIES),
    days: z.number(),
    note: z.string().nullable(),
    createdAt: z.string(),
});
export const leaveBalanceItemSchema = z.object({
    leaveTypeId: uuid,
    code: z.string(),
    name: z.string(),
    /** Σ of the year's entries. */
    balance: z.number(),
    entries: z.array(leaveLedgerEntrySchema),
});
export const leaveBalanceResponseSchema = z.object({
    employeeId: uuid,
    year: z.int(),
    items: z.array(leaveBalanceItemSchema),
});
/** `POST /leave/balances/adjust`: this leave year; signed, half-day steps, with a reason. */
export const leaveAdjustSchema = z.object({
    employeeId: uuid,
    leaveTypeId: uuid,
    days: z
        .number()
        .min(-366)
        .max(366)
        .refine((d) => d !== 0 && Number.isInteger(d * 2), 'Use whole or half days, not 0'),
    note: z.string().trim().min(3).max(500),
});
/** `POST /leave/balances/import?dryRun=`: XLSX upload of opening balances (FR-LEAVE-3). */
export const leaveImportQuerySchema = z.object({ dryRun: queryBoolean.default(true) });
export const leaveImportResultSchema = z.object({
    dryRun: z.boolean(),
    year: z.int(),
    /** OPENING entries the file gives (written unless `dryRun` or errors). */
    entries: z.int().min(0),
    /** Rows left out because their days are 0. */
    skipped: z.int().min(0),
    /** Spreadsheet row numbers (header = row 1) with what is wrong. */
    errors: z.array(z.object({ row: z.int(), message: z.string() })),
});
// ── Requests (FR-LEAVE-4…7, ADR-077) ──────────────────────────────────────────────────────────
/** Form fields arrive as strings (multipart): empty → absent. */
const optional = (schema) => z.preprocess((v) => (v === '' || v === null ? undefined : v), schema.optional());
/**
 * `POST /leave/requests` (JSON, or multipart with a `file` — the medical document). Without
 * `employeeId` it is the caller's own request (leave.request, goes to approval); with it, HR
 * records leave for that employee (leave.manage, approved at once — never for oneself).
 */
export const leaveRequestCreateSchema = z.object({
    employeeId: optional(uuid),
    leaveTypeId: uuid,
    fromDate: isoDate,
    toDate: isoDate,
    /** The first day starts after lunch. */
    startHalf: optional(z.literal('SECOND_HALF')),
    /** The last day ends at lunch. */
    endHalf: optional(z.literal('FIRST_HALF')),
    reason: optional(z.string().trim().max(500)),
});
export const leaveRequestDocumentSchema = z.object({
    id: uuid,
    originalName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.int(),
});
export const leaveRequestItemSchema = z.object({
    id: uuid,
    employeeId: uuid,
    leaveTypeId: uuid,
    leaveTypeCode: z.string(),
    leaveTypeName: z.string(),
    fromDate: isoDate,
    toDate: isoDate,
    startHalf: z.literal('SECOND_HALF').nullable(),
    endHalf: z.literal('FIRST_HALF').nullable(),
    days: z.number(),
    reason: z.string().nullable(),
    status: z.enum(LEAVE_REQUEST_STATUSES),
    /** The approval request; null when HR recorded the leave. */
    approvalRequestId: uuid.nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    /**
     * Medical documents (AC-10): only for the employee, `sensitive.read.all` and — while pending —
     * the reporting manager deciding it (ADR-078); else absent.
     */
    documents: z.array(leaveRequestDocumentSchema).optional(),
});
/** `POST /leave/requests/:id/cancel`: a reason is required when HR cancels (FR-LEAVE-6). */
export const leaveCancelSchema = z.object({ reason: optional(z.string().trim().min(3).max(500)) });
/** `POST /leave/requests/:id/shorten` (leave.manage): a new, earlier end with a reason. */
export const leaveShortenSchema = z.object({
    toDate: isoDate,
    endHalf: optional(z.literal('FIRST_HALF')),
    reason: z.string().trim().min(3).max(500),
});
export const leaveDocumentParamsSchema = z.object({ id: uuid, docId: uuid });
// ── Views (FR-LEAVE-8, T-074, ADR-078) ───────────────────────────────────────────────────────
const leaveYear = z.coerce.number().int().min(2000).max(2100);
/** `GET /me/leave?year=` (default this year). */
export const myLeaveQuerySchema = z.object({ year: leaveYear.optional() });
/**
 * One leave type as the employee sees it: what the apply dialog needs (day rules, document
 * rule, maximum) and the year's figures. `balance` / `available` are null for types without a
 * balance (ML, PL, LWP); available = balance − pending.
 */
export const myLeaveTypeSchema = z.object({
    leaveTypeId: uuid,
    code: z.string(),
    name: z.string(),
    paid: z.boolean(),
    countBasis: z.enum(LEAVE_COUNT_BASES),
    allowHalfDay: z.boolean(),
    documentAboveDays: z.number().nullable(),
    maxDaysPerRequest: z.number().nullable(),
    hasBalance: z.boolean(),
    /** Σ of the year's ledger entries. */
    balance: z.number().nullable(),
    /** Days reserved by pending requests of the year. */
    pending: z.number(),
    available: z.number().nullable(),
    /** Days of approved leave in the year. */
    taken: z.number(),
    /** Why the employee may not take it today (gender, service, confirmation); null = may. */
    ineligible: z.string().nullable(),
});
export const myLeaveResponseSchema = z.object({
    employeeId: uuid,
    year: z.int(),
    types: z.array(myLeaveTypeSchema),
});
/** Whose requests: the caller's, their reporting tree's (`team.read`) or everyone's (`leave.read.all`). */
export const LEAVE_REQUEST_SCOPES = ['me', 'team', 'all'];
/** `GET /leave/requests`: newest first; `from` / `to` keep requests overlapping that range. */
export const leaveRequestListQuerySchema = paginationQuerySchema.extend({
    scope: z.enum(LEAVE_REQUEST_SCOPES).default('me'),
    status: queryList(z.enum(LEAVE_REQUEST_STATUSES)).optional(),
    leaveTypeId: uuid.optional(),
    employeeId: uuid.optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** Name or employee code. */
    q: z.string().trim().max(100).optional(),
});
const leavePerson = z.object({ id: uuid, empCode: z.int(), fullName: z.string() });
/**
 * A listed request: the request, whose it is, and what this viewer may do with it now
 * (decided by the API, FR-LEAVE-6): `canCancel` (with `cancelNeedsReason` when it is not the
 * employee's own), `canShorten` (leave.manage, approved, not one's own).
 */
export const leaveRequestListItemSchema = leaveRequestItemSchema.extend({
    employee: leavePerson,
    requestedAt: z.string(),
    canCancel: z.boolean(),
    cancelNeedsReason: z.boolean(),
    canShorten: z.boolean(),
});
export const leaveRequestListResponseSchema = paginated(leaveRequestListItemSchema);
/** `GET /leave/calendar?scope=team|all&from=&to=`: at most 62 days. */
export const leaveCalendarQuerySchema = z
    .object({
    scope: z.enum(['team', 'all']).default('team'),
    from: isoDate,
    to: isoDate,
})
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'Must not be before `from`' })
    .refine((v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 < 62, {
    path: ['to'],
    message: 'At most 62 days',
});
/** Pending and approved leave overlapping the range; no reasons or documents. */
export const leaveCalendarItemSchema = z.object({
    id: uuid,
    employee: leavePerson,
    leaveTypeCode: z.string(),
    leaveTypeName: z.string(),
    fromDate: isoDate,
    toDate: isoDate,
    startHalf: z.literal('SECOND_HALF').nullable(),
    endHalf: z.literal('FIRST_HALF').nullable(),
    days: z.number(),
    status: z.enum(['PENDING', 'APPROVED']),
});
export const leaveCalendarResponseSchema = z.object({
    from: isoDate,
    to: isoDate,
    items: z.array(leaveCalendarItemSchema),
});
/** `GET /exports/leave-balances.xlsx?year=` (leave.read.all). */
export const leaveBalanceExportQuerySchema = z.object({ year: leaveYear.optional() });
export const leaveApprovalParamSchema = z.object({ approvalId: uuid });
//# sourceMappingURL=leave.js.map